import { Core } from "@faire/design-tokens";
import { TrackedLinkContext } from "@faire/retailer-visitor-shared/components/TrackedLink/index";
import { IProductAddOptions } from "@faire/retailer-visitor-shared/features/ProductAdd/ProductAddOptions";
import { useCartProductAddOptions } from "@faire/retailer-visitor-shared/features/ProductAdd/useCartProductAddOptions";
import { LazyQuickAddModal } from "@faire/retailer-visitor-shared/features/ProductQuickAddModal/LazyQuickAddModal";
import { useCartItems } from "@faire/retailer-visitor-shared/hooks/cart/useCartItems";
import { useCartUpdate } from "@faire/retailer-visitor-shared/hooks/cart/useCartUpdate";
import { setTimeoutIfPossible } from "@faire/retailer-visitor-shared/lib/async-functions/setTimeoutIfPossible";
import { useCanAddToCart } from "@faire/retailer-visitor-shared/lib/useCanAddToCart";
import {
  ICartItemForUpdate,
  UpdateCartHandler,
} from "@faire/retailer-visitor-shared/stores/domain/CartStore/consts";
import { AddToCartVariants } from "@faire/retailer-visitor-shared/stores/domain/CartStore/consts";
import { getWindowOrThrow } from "@faire/web--source/common/globals/getWindow";
import { logAssert } from "@faire/web--source/common/logging";
import { trackEvent } from "@faire/web--source/common/trackEvent";
import { ColorOrKey } from "@faire/web--source/slate/Color";
import { Row } from "@faire/web--source/slate/Layout";
import { LoadingSpinner } from "@faire/web--source/slate/LoadingSpinner";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { TypographyStyles } from "@faire/web--source/slate/Typography";
import { ShakeAnimation } from "@faire/web--source/ui/Shakeable";
import { IQuickAdd } from "@faire/web-api--source/indigofair/data/IQuickAdd";
import React, {
  CSSProperties,
  Suspense,
  useContext,
  useState,
  type JSX,
} from "react";
import styled, { css } from "styled-components";

interface IQuickAddProps {
  productToken: string | undefined;
  brandToken: string | undefined;
  quickAddData: IQuickAdd;
  onChooseOption?: () => void;
  isHighlightedProductForPromotionalEvent?: boolean;

  /** Callback invoked as an option is quickly added to the cart. */
  onQuickAddOption?: () => void;

  /** Callback invoked after the choose options dialog is closed. */
  onModalClose?: () => void;

  style?: CSSProperties;
  className?: string;
  forceShowModal?: boolean;

  productAddOptions?: IProductAddOptions;
  /** Custom button. quickAddData.quick_add_text, style, and className are ignored if this is provided. */
  button?: {
    (props: {
      shake: boolean;
      loading: boolean;
      onClick: (e: React.MouseEvent<HTMLElement>) => void;
    }): JSX.Element;
  };

  // TODO: Pass in updateCart handler instead of passing in prdouctAddOptions
  updateCartFn?: UpdateCartHandler;
}

export const QuickAdd: React.FC<IQuickAddProps> = ({
  productToken,
  brandToken,
  quickAddData,
  onChooseOption,
  onQuickAddOption,
  onModalClose,
  style,
  className,
  forceShowModal,
  productAddOptions,
  button,
  updateCartFn,
}) => {
  const [isQuickAddModalOpen, setIsQuickAddModalOpen] = useState(false);
  const [notAvailable, setNotAvailable] = useState(false);

  const context = useContext(TrackedLinkContext);

  const { getItemQuantityInCart } = useCartItems();
  const { updateCartItems, isPending: isQuickAddOptionLoading } =
    useCartUpdate();

  const cartProductAddOptions = useCartProductAddOptions();
  const addOptions = productAddOptions ?? cartProductAddOptions;

  const canAddToCart = useCanAddToCart();

  const preventClickEvents = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onOpenModal = (e: React.MouseEvent<HTMLElement>) => {
    preventClickEvents(e);
    onChooseOption?.();
    trackEvent(`/choose-options/${productToken}`);
    if (context) {
      trackEvent(
        `/container/${context.containerName}/choose-options/${productToken}`
      );
    }
    setIsQuickAddModalOpen(true);
  };

  const onCloseModal = () => {
    setIsQuickAddModalOpen(false);

    onModalClose?.();
  };

  const setAsUnavailable = () => {
    setNotAvailable(true);
    setTimeoutIfPossible(() => setNotAvailable(false), 2000);
  };

  const onQuickAdd = async (
    event: React.MouseEvent<HTMLElement>
  ): Promise<void> => {
    preventClickEvents(event);

    if (!canAddToCart) {
      setAsUnavailable();
      return;
    }

    if (!quickAddData.quick_add_option) {
      return;
    }

    try {
      const addedOptionToken = await handleQuickAddOption();
      if (addedOptionToken) {
        trackEvent(`/quick-add/${addedOptionToken}`);
        if (context) {
          trackEvent(
            `/container/${context.containerName}/quick_add/${addedOptionToken}`
          );
        }
      } else {
        setAsUnavailable();
      }
    } catch {
      setAsUnavailable();
    }
  };

  const handleQuickAddOption = async (): Promise<string | undefined> => {
    logAssert(
      !!quickAddData.quick_add_option,
      "Undefined quick_add_option field on quick add to cart"
    );
    const {
      option_token,
      option_unit_multiplier = 0,
      option_min_order_quantity = 1,
      option_available_units,
    } = quickAddData.quick_add_option;
    logAssert(!!option_token, "Undefined option token on quick add to cart");
    const cartUpdateStartTime = getWindowOrThrow().performance.now();

    // Check that there is sufficient inventory for quantity already added + newly added quantity.
    const existingQuantity = getItemQuantityInCart(option_token);
    const newQuantity = Math.max(
      existingQuantity + option_unit_multiplier,
      option_min_order_quantity
    );
    // option_available_units can be unset, this is to be interpreted as there are no limits.
    if (
      option_available_units != null &&
      newQuantity > option_available_units
    ) {
      return;
    }

    onQuickAddOption?.();

    // Add to cart.
    const addedItemData = [
      ICartItemForUpdate.build({
        product_token: productToken,
        brand_token: brandToken,
        product_option_token: option_token,
        quantity: newQuantity,
        customizations: [],
      }),
    ];
    await updateCartItems({
      items: addedItemData,
      upltParams: {
        event: "add-to-cart",
        startTime: cartUpdateStartTime,
        variant: AddToCartVariants.QUICK_ADD_BTN,
      },
    });

    return option_token;
  };

  const handleClick = () => {
    return (e: React.MouseEvent<HTMLElement>) => {
      if (isQuickAddOptionLoading) {
        return preventClickEvents(e);
      }

      if (quickAddData.quick_add_option && !forceShowModal) {
        return onQuickAdd(e);
      }

      return onOpenModal(e);
    };
  };

  return (
    <>
      {productToken && isQuickAddModalOpen ? (
        <Suspense fallback={null}>
          <LazyQuickAddModal
            onClose={onCloseModal}
            productToken={productToken}
            productAddOptions={addOptions}
            updateCartFn={updateCartFn}
          />
        </Suspense>
      ) : null}
      {button ? (
        button({
          shake: notAvailable,
          loading: isQuickAddOptionLoading,
          onClick: handleClick(),
        })
      ) : (
        <QuickAddButton
          $shake={notAvailable}
          className={className}
          style={style}
          onClick={handleClick()}
          data-test-id="quick-add-marketplace-product"
        >
          {isQuickAddOptionLoading ? (
            <LoadingWrapper>
              <LoadingSpinner size="small" />
            </LoadingWrapper>
          ) : null}
          <TextWrapper $isLoading={isQuickAddOptionLoading}>
            <Typography align="center">
              {quickAddData.quick_add_text}
            </Typography>
          </TextWrapper>
        </QuickAddButton>
      )}
    </>
  );
};

const commonAddButtonCss = css<{ $shake?: boolean }>`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  overflow: hidden;
  background-color: ${Core.surface.inverse};
  border: 1px solid ${Core.border.muted};
  cursor: pointer;
  pointer-events: auto;

  ${responsiveMediaQueries.mobile} {
    display: none;
  }

  ${(props) => (props.$shake ? ShakeAnimation("-50%") : "")};
`;

export const QuickAddButton = styled(Row).attrs({
  align: "center",
  justify: "center",
})<{ $shake?: boolean; $color?: ColorOrKey }>`
  width: calc(100% - 16px);
  min-height: 30px;
  height: auto;

  ${TypographyStyles({ as: undefined })};
  color: ${Core.text.default};
  text-align: center;

  ${commonAddButtonCss}
`;

const TextWrapper = styled.div<{ $isLoading: boolean }>`
  ${(props) =>
    props.$isLoading
      ? css`
          visibility: hidden;
        `
      : ""};
  width: 100%;
`;

const LoadingWrapper = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;
